
import React, { useState, useEffect } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { getAttendanceWithPagination, deleteAttendance, checkOutById } from "adminApi/HumanResources/Attendance";
import DataTable from "Common/DataTable/DataTable";
import { toast } from "react-hot-toast";
import ActionWrapper from "Common/DataTable/ActionWrapper";
import EditBtn from "Common/DataTable/EditBtn";
import DeleteBtn from "Common/DataTable/DeleteBtn";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/Attendance.json";

const AttendanceListTable = ({ refreshTrigger }) => {
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const debouncedSearch = useDebouncedValue(search, 500);
    const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
    const sizeOptions = [10, 20, 50, 100];
    const { tabledata } = fields

    // Fetch Hook
    const { data, meta, loading, error, refetch } = usePaginatedFetch(getAttendanceWithPagination, {
        page,
        limit,
        search: debouncedSearch,
        title: "Attendance List",
    });

    // Handle external refresh trigger
    useEffect(() => {
        if (refreshTrigger > 0) {
            refetch();
        }
    }, [refreshTrigger, refetch]);

    // Auto-generate display fields
    const displayFields = generateDisplayFields(tabledata);

    // Table Hook
    const { formattedData, columns: baseColumns } = useTableConfig({
        data: data,
        page,
        limit,
        canShowAction,
        refetch,
        config: {
            entityName: "Attendance",
            editUrl: "attendance-edit",
            idKey: "id",
            deleteApi: deleteAttendance,
            displayFields,
        },
    });

    // Custom columns to handle badges, high-precision duration and actions
    const columns = baseColumns.map(col => {
        if (col.Header === "Status") {
            return {
                ...col,
                Cell: ({ value }) => {
                    if (!value) return null;
                    const statusConfig = {
                        present: { bg: "bg-success", label: "Present" },
                        absent: { bg: "bg-danger", label: "Absent" },
                        leave: { bg: "bg-warning text-dark", label: "Leave" },
                        half_day: { bg: "bg-info text-dark", label: "Half Day" }
                    };
                    const config = statusConfig[value] || { bg: "bg-secondary", label: value };
                    return <span className={`badge ${config.bg}`}>{config.label}</span>;
                }
            };
        }

        if (col.Header === "Sign In" || col.Header === "Sign Out") {
            return {
                ...col,
                Cell: ({ value }) => {
                    if (!value || value === "---") return "---";
                    try {
                        const date = new Date(value);
                        return date.toLocaleTimeString('en-GB', { hour12: false });
                    } catch (e) {
                        return value;
                    }
                }
            };
        }

        // Use accessor if possible for more stable matching
        if (col.accessor === "total_hours" || col.Header === "Hours" || col.Header === "Total Hours") {
            return {
                ...col,
                Header: "Duration",
                Cell: ({ value }) => {
                    if (!value || value === "---" || value === "00:00:00" || value == 0) return "---";
                    return value;
                }
            };
        }

        if (col.Header && col.Header.toString().includes("Action")) {
            return {
                ...col,
                Cell: ({ row }) => {
                    const item = row?.original;

                    const handleTableCheckOut = async () => {
                        try {
                            const res = await checkOutById(item.id);
                            if (res.success) {
                                toast.success("Checked out successfully");
                                refetch();
                            } else {
                                toast.error(res.error || "Check-out failed");
                            }
                        } catch (err) {
                            toast.error("An error occurred");
                        }
                    };

                    return (
                        <ActionWrapper>
                            {item["Sign Out"] == null && (
                                <span className="tooltip-wrapper tooltip-top">
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm border me-1 rounded-2"
                                        title="Check Out"
                                        aria-label="Check Out"
                                        onClick={handleTableCheckOut}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg"
                                            width={20}
                                            height={20}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="svg-stroke">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M10 8v-2a2 2 0 0 1 2 -2h7a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-2" />
                                            <path d="M15 12h-12l3 -3" />
                                            <path d="M6 15l-3 -3" />
                                        </svg>
                                    </button>
                                    <span className="tooltip-text">Check Out</span>
                                </span>
                            )}
                            <EditBtn url={`attendance-edit/${item.id}`} />
                            <DeleteBtn
                                id={item.id}
                                fetchData={refetch}
                                apiDeleteFunction={deleteAttendance}
                                itemName="Attendance"
                            />
                        </ActionWrapper>
                    );
                }
            };
        }

        return col;
    });

    const handleSearch = (val, resetPage = null) => {
        setSearch(val);
        if (resetPage !== null) setPage(resetPage);
    };

    const onPageChange = (p) => { setPage(p) };

    const onLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    return (
        <DataTable
            columns={columns}
            data={formattedData}
            options={{
                loading,
                error,
                sortableColumns: displayFields.map((f) => f.accessor),
            }}
            Search={{
                search: true,
                SearchInputValue: search,
                handleSearch: handleSearch,
            }}
            Entries={{
                Entries: true,
                limit,
                onLimitChange,
                options: sizeOptions,
            }}
            pagination={{
                isActive: true,
                meta,
                onPageChange,
            }}
        />
    );
};

export default AttendanceListTable;
